'use client'

import { useCallback, useMemo, useSyncExternalStore } from 'react'
import { fetchMore, feedPath, getServerSnapshot, getSnapshot, subscribe, type FeedState } from './store'

export type { FeedRow, FeedState } from './store'

/**
 * A live sales feed: a collection's (`slug`) or the whole market's (no
 * slug). Rows arrive merged, ordered and deduplicated by the API and stay
 * current over one shared stream; `fetchMore` pages back through history.
 */
export function useSalesFeed(slug?: string | null, enabled = true): FeedState & { fetchMore: () => void } {
	const path = feedPath(slug)
	const sub = useCallback(
		(listener: () => void) => (enabled ? subscribe(path, listener) : () => {}),
		[path, enabled]
	)
	const state = useSyncExternalStore(sub, () => getSnapshot(path), getServerSnapshot)
	const more = useCallback(() => void fetchMore(path), [path])
	return { ...state, fetchMore: more }
}

/**
 * A collection's confirmed sales from the live feed, in the per-sale shape
 * the older `/sales` consumers read (`block_timestamp` in seconds). Shares
 * the feed's stream, so a header and the activity panel cost one request.
 */
export function useConfirmedCollectionSales(slug?: string | null) {
	const { rows } = useSalesFeed(slug, !!slug)
	return useMemo(
		() =>
			rows
				.filter((r) => r.status === 'confirmed')
				.map((r) => ({
					txid: r.txid ?? '',
					inscription_id: r.inscription_id ?? '',
					block_timestamp: Math.floor(r.ts / 1000),
					block_height: r.block_height ?? 0,
					price_sats: Number(r.price_sats) || 0,
					marketplace: typeof r.marketplace === 'number' ? r.marketplace : 0,
					signals: Number(r.signals) || 0,
					sighash: Number(r.sighash) || 0,
					inscriptions_in_tx: r.inscriptions_in_tx ?? 1,
					seller_address: r.seller_address ?? null,
					buyer_address: r.buyer_address ?? null
				})),
		[rows]
	)
}
